import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';

import { ParticipantMarketType } from '@cds/betting-offer';
import { AutomationModule } from '@frontend/sports/automation/core-feature';
import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { CustomActiveStateDirective } from '@frontend/sports/common/ui/custom-active-state';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { WinGameFixture, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { Store } from '@ngrx/store';
import { ExternalBetslipActions } from 'packages/sports/common/betslip/core/external-betslip-actions';
import { PickId, V1PickId, V2OptionMarketPickId, V2ParticipantPickId } from 'packages/sports/common/betslip/core/picks/pick-id';
import { ParticipantPickType, PriceType } from 'packages/sports/common/betslip/core/picks/pick-models';
import { BetslipIntegrationService } from 'packages/sports/common/betslip/integration/betslip-integration.service';

import { GeolocationCheckService } from '../geo-location/geo-location-check.service';
import { HorsesFixtureParticipantHelper } from '../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { OptionIndicatorDirective } from './option-indicator.directive';
import { PickSourceProvider } from './pick-source.provider';
import { PickComponent } from './pick.component';

@Component({
    selector: 'ms-race-xcast-pick',
    templateUrl: 'pick.component.html',
    imports: [CustomActiveStateDirective, OptionIndicatorDirective, AutomationModule, AsyncPipe],
})
export class RaceXCastPickComponent extends PickComponent implements OnInit, OnChanges, OnDestroy {
    @Input() participants: WinGameParticipant[];
    @Input() fixture: WinGameFixture;
    @Input() marketType: ParticipantMarketType;
    @Input() pickType: ParticipantPickType;
    @Output() selected = new EventEmitter<boolean>();
    @Input() optionGroup?: EventOptionGroup;
    @Input() participantOptionGroups?: { [id: string]: EventOption[] } = {};
    @Input() trackingOptions: { [key: string]: string };

    override pickId: V2ParticipantPickId | V2OptionMarketPickId;

    constructor(
        changeDetector: ChangeDetectorRef,
        geolocationCheckService: GeolocationCheckService,
        private pickSourceProvider: PickSourceProvider,
        betslipIntegration: BetslipIntegrationService,
        private horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        private store: Store,
    ) {
        super(geolocationCheckService, betslipIntegration, changeDetector, null);
    }

    override ngOnDestroy(): void {
        this.pickSelectedSubscription?.unsubscribe();
        super.ngOnDestroy();
    }

    setParticipantOptionGroups() {
        this.participantOptionGroups = {};
        if (this.optionGroup) {
            this.participantOptionGroups = this.horsesFixtureParticipantHelper.getParticipantOptionGroups(
                this.optionGroup,
                this.fixture.participants,
            );
        }
    }

    ngOnChanges(): void {
        this.setParticipantOptionGroups();
        const pickId =
            this.fixture.isOptionBet && this.participantOptionGroups
                ? this.horsesFixtureParticipantHelper.createOptionPick(
                      this.fixture,
                      this.participants,
                      this.marketType,
                      this.pickType,
                      this.participantOptionGroups,
                      this.optionGroup,
                  )
                : this.horsesFixtureParticipantHelper.createParticipantPick(this.fixture, this.participants, this.marketType, this.pickType);
        if (pickId) {
            this.setPickId(pickId);
        }
    }

    protected override setPickId(pickId: PickId, priceType?: PriceType, longId?: string): void {
        if (!this.pickId || !this.pickId.isEqual(pickId)) {
            this.pickId = !this.fixture.isOptionBet ? (pickId as V2ParticipantPickId) : (pickId as V2OptionMarketPickId);

            this.pickSelectedSubscription?.unsubscribe();

            const pickSelected$ =
                this.lineSwitcherPickHandlerService?.pickSelected$(pickId as V1PickId | V2OptionMarketPickId | V2OptionMarketPickId) ??
                this.betslipIntegration.pickSelected$(pickId, priceType, longId);

            this.pickSelectedSubscription = pickSelected$.subscribe((selected) => {
                const isChanged = this.state.selected !== selected;
                if (isChanged) {
                    this.state.selected = selected;
                    this.changeDetection.detectChanges();
                }
                this.selected.emit(this.state.selected);
            });
        }
    }

    get price(): NativePrice {
        return NativePrice.empty();
    }

    get priceId(): number | undefined {
        return;
    }

    get isOnline(): boolean {
        return false;
    }

    protected addPick(): void {
        if (!this.pickId) {
            return;
        }

        const pickSource = this.pickSourceProvider.get(this.trackingSource, this.trackingPosition);
        if (this.trackingOptions !== undefined) {
            pickSource.source = this.trackingSource;
        }
        pickSource.additional = this.trackingOptions;
        this.store.dispatch(
            ExternalBetslipActions.addPick({
                pickId: this.pickId,
                tracking: pickSource,
            }),
        );
    }

    protected removePick(): void {
        this.store.dispatch(ExternalBetslipActions.removePick({ pickId: this.pickId }));
    }
}
